import { Plus, ShieldCheck } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { problemMessage } from '../api/problem';
import { InvitePanel } from '../invitations/InvitePanel';
import { usePlaceContext } from '../place/PlaceLayout';
import { apiPlace, type Place } from '../place/place';
import { PageHead } from '../ui/PageHead';
import { PhonePrimary } from '../ui/PhonePrimary';
import { StateMessage } from '../ui/StateMessage';
import { GrantRolesPanel, GrantToPeoplePanel } from './GrantPanels';
import { isHere, PeopleWithRoles, RoleDetail, RoleList, type RoleEntry } from './RoleParts';
import {
  useAssignableRoles,
  type AssignableRole,
  type AssignableRolesState,
} from './use-assignable-roles';
import { useRoleAssignments, type RoleAssignment } from './use-role-assignments';

type SetParams = ReturnType<typeof useSearchParams>[1];
type Granting = null | { roleId: string | null };
type GrantingTo = null | { id: string; displayName: string };
type ParamChange = Record<string, string | null>;

/** The quick search's «Concedi ruolo» lands here with ?grant=1: open the panel, once. */
function useOpenGrantFromLink(
  params: URLSearchParams,
  setParams: SetParams,
  canGrantHere: boolean,
  setGranting: (next: Granting) => void,
) {
  useEffect(() => {
    if (params.get('grant') === '1' && canGrantHere) {
      setGranting({ roleId: null });
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('grant');
          return next;
        },
        { replace: true },
      );
    }
  }, [params, setParams, canGrantHere, setGranting]);
}

/** The roles of the place: the ones this caller may assign, and the ones held here only. */
function useRoleEntries(assignable: readonly AssignableRole[], items: readonly RoleAssignment[]) {
  return useMemo<RoleEntry[]>(() => {
    const byId = new Map<string, RoleEntry>();
    for (const role of assignable) {
      byId.set(role.id, {
        id: role.id,
        code: role.code,
        name: role.name,
        moduleCode: role.module?.code ?? 'administration',
        assignable: role,
      });
    }
    for (const grant of items) {
      if (!byId.has(grant.role.id)) {
        byId.set(grant.role.id, { ...grant.role, moduleCode: null, assignable: null });
      }
    }
    return [...byId.values()];
  }, [assignable, items]);
}

function useHoldersByRole(items: readonly RoleAssignment[]) {
  return useMemo(() => {
    const map = new Map<string, RoleAssignment[]>();
    for (const grant of items) {
      map.set(grant.role.id, [...(map.get(grant.role.id) ?? []), grant]);
    }
    return map;
  }, [items]);
}

function useParamSetter(setParams: SetParams) {
  return (next: ParamChange) =>
    setParams(
      (current) => {
        const merged = new URLSearchParams(current);
        for (const [key, value] of Object.entries(next)) {
          if (value === null) {
            merged.delete(key);
          } else {
            merged.set(key, value);
          }
        }
        return merged;
      },
      { replace: true },
    );
}

function RolesHead({
  titleId,
  placeName,
  view,
  set,
  canGrant,
  onGrant,
}: {
  readonly titleId: string;
  readonly placeName: string;
  readonly view: 'role' | 'person';
  readonly set: (next: ParamChange) => void;
  readonly canGrant: boolean;
  readonly onGrant: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <PageHead
      titleId={titleId}
      title={t('rolesPage.title')}
      summary={t('rolesPage.summary', { place: placeName })}
      actions={
        <>
          <div role="group" aria-label={t('rolesPage.viewLabel')} className="view-switch">
            <button
              type="button"
              aria-pressed={view === 'role'}
              onClick={() => set({ view: null })}
            >
              {t('rolesPage.byRole')}
            </button>
            <button
              type="button"
              aria-pressed={view === 'person'}
              onClick={() => set({ view: 'person', role: null })}
            >
              {t('rolesPage.byPerson')}
            </button>
          </div>
          {canGrant ? (
            <span className="page-primary">
              <Button onClick={onGrant}>
                <Plus />
                {t('rolesPage.grant')}
              </Button>
            </span>
          ) : null}
        </>
      }
    />
  );
}

type GrantsState = ReturnType<typeof useRoleAssignments>['state'];

type BodyProps = {
  readonly grants: GrantsState;
  readonly items: readonly RoleAssignment[];
  readonly view: 'role' | 'person';
  readonly reload: () => void;
  readonly roles: readonly RoleEntry[];
  readonly holdersByRole: ReadonlyMap<string, readonly RoleAssignment[]>;
  readonly selected: RoleEntry | null;
  readonly set: (next: ParamChange) => void;
  readonly place: Place;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
  readonly canGrant: boolean;
  readonly assignableKnown: boolean;
  readonly onGrantRole: (roleId: string) => void;
  readonly onGrantPerson: (person: { id: string; displayName: string }) => void;
  readonly onStanding: () => void;
};

function RolesByRole(props: BodyProps) {
  const { roles, holdersByRole, selected, set, place, placeKind, placeId, placeName } = props;
  return (
    <div className={cn('roles-layout', selected !== null && 'roles-layout-with-detail')}>
      <RoleList
        roles={roles}
        holdersByRole={holdersByRole}
        selectedId={selected?.id ?? null}
        onSelect={(id) => set({ role: id })}
        placeName={placeName}
      />
      {selected !== null ? (
        <RoleDetail
          role={selected}
          holders={holdersByRole.get(selected.id) ?? []}
          place={place}
          placeKind={placeKind}
          placeId={placeId}
          placeName={placeName}
          canGrant={props.canGrant && selected.assignable !== null}
          assignableKnown={props.assignableKnown}
          onGrant={() => props.onGrantRole(selected.id)}
          onBack={() => set({ role: null })}
          onChanged={props.reload}
          onStanding={props.onStanding}
        />
      ) : null}
    </div>
  );
}

function RolesBody(props: BodyProps) {
  const { grants, items, view, reload, placeName, canGrant, onGrantPerson } = props;
  const { t } = useTranslation('administration');
  if (grants.status === 'loading' && items.length === 0) {
    return (
      <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-2">
        {[0, 1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-14 w-full" />
        ))}
      </SkeletonRegion>
    );
  }
  if (grants.status === 'error') {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={ShieldCheck}
        title={t('rolesPage.loadError')}
        text={problemMessage(t, grants.code, grants.httpStatus)}
        action={
          <Button variant="outline" onClick={reload}>
            {t('common.retry')}
          </Button>
        }
      />
    );
  }
  if (view !== 'role') {
    return (
      <PeopleWithRoles
        grants={items}
        placeName={placeName}
        canGrant={canGrant}
        onGrant={onGrantPerson}
      />
    );
  }
  return <RolesByRole {...props} />;
}

type PanelsProps = {
  readonly granting: Granting;
  readonly setGranting: (next: Granting) => void;
  readonly grantingTo: GrantingTo;
  readonly setGrantingTo: (next: GrantingTo) => void;
  readonly assignable: AssignableRolesState;
  readonly retryAssignable: () => void;
  readonly holdersByRole: ReadonlyMap<string, readonly RoleAssignment[]>;
  readonly items: readonly RoleAssignment[];
  readonly place: Place;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
  readonly canGrant: boolean;
  readonly canInvite: boolean;
  readonly inviting: boolean;
  readonly setInviting: (open: boolean) => void;
  readonly reload: () => void;
  readonly onStanding: () => void;
  readonly phoneRoleId: string | null;
};

function GrantToPeople(props: PanelsProps) {
  const { granting, setGranting, assignable, holdersByRole, place, canInvite } = props;
  const grantingRoleId = granting?.roleId ?? null;
  return (
    <GrantToPeoplePanel
      open={granting !== null}
      onOpenChange={(open) => {
        if (!open) {
          setGranting(null);
        }
      }}
      role={assignable.roles.find((role) => role.id === grantingRoleId) ?? null}
      roles={assignable}
      onRetryRoles={props.retryAssignable}
      onRoleChange={(roleId) => setGranting({ roleId })}
      holderIds={
        new Set(
          (holdersByRole.get(grantingRoleId ?? '') ?? [])
            .filter((grant) => grant.via === 'direct' && isHere(grant, place))
            .map((grant) => grant.user.id),
        )
      }
      onGranted={props.reload}
      onStanding={props.onStanding}
      {...(canInvite
        ? {
            onInvite: () => {
              setGranting(null);
              props.setInviting(true);
            },
          }
        : {})}
      placeKind={props.placeKind}
      placeId={props.placeId}
      placeName={props.placeName}
    />
  );
}

function GrantToPerson(props: PanelsProps & { readonly grantingTo: NonNullable<GrantingTo> }) {
  const { grantingTo, items, place } = props;
  return (
    <GrantRolesPanel
      open
      onOpenChange={(open) => {
        if (!open) {
          props.setGrantingTo(null);
        }
      }}
      person={grantingTo}
      heldRoleIds={
        new Set(
          items
            .filter(
              (grant) =>
                grant.user.id === grantingTo.id && grant.via === 'direct' && isHere(grant, place),
            )
            .map((grant) => grant.role.id),
        )
      }
      onGranted={props.reload}
      onStanding={props.onStanding}
      placeKind={props.placeKind}
      placeId={props.placeId}
      placeName={props.placeName}
    />
  );
}

function RolesPanels(props: PanelsProps) {
  const { t } = useTranslation('administration');
  const { canGrant, grantingTo } = props;
  return (
    <>
      {canGrant ? (
        <PhonePrimary>
          <Button onClick={() => props.setGranting({ roleId: props.phoneRoleId })}>
            <Plus />
            {t('rolesPage.grant')}
          </Button>
        </PhonePrimary>
      ) : null}
      {canGrant ? <GrantToPeople {...props} /> : null}
      {canGrant && grantingTo !== null ? (
        <GrantToPerson {...props} grantingTo={grantingTo} />
      ) : null}
      {props.canInvite ? (
        <InvitePanel
          open={props.inviting}
          onOpenChange={props.setInviting}
          placeKind={props.placeKind}
          placeId={props.placeId}
          placeName={props.placeName}
          onInvited={props.reload}
        />
      ) : null}
    </>
  );
}

function RolesAlerts({ show, onRetry }: { readonly show: boolean; readonly onRetry: () => void }) {
  const { t } = useTranslation('administration');
  if (!show) return null;
  return (
    <p role="alert" className="flex flex-wrap items-center gap-3 text-sm">
      {t('rolesPage.assignableError')}
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('common.retry')}
      </Button>
    </p>
  );
}

function RolesIncomplete({ show }: { readonly show: boolean }) {
  const { t } = useTranslation('administration');
  if (!show) return null;
  return <p className="text-muted-foreground text-[13px]">{t('rolesPage.incomplete')}</p>;
}

/**
 * Ruoli e accessi (FR-AD-5): the same facts by role and by person, from one read of the place's
 * grants, so the two views and their counts always agree. Roles are granted from either side
 * through the many-to-many panel, and revoked with the × on a grant this caller may revoke. A
 * revocation takes the derogated confirmation toast, without undo, because granting again is the
 * reversal (FR-UX-5). The view and the chosen role live in the address.
 */
export function RolesPage() {
  const { place, detail, refreshPlace } = usePlaceContext();
  const pair = apiPlace(place);
  const placeKind = pair?.placeKind ?? 'organizations';
  const placeId = pair?.placeId ?? '';
  const placeName = detail.place.name;
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'person' ? 'person' : 'role';
  const selectedRoleId = params.get('role');
  const { state: assignable, retry: retryAssignable } = useAssignableRoles(placeKind, placeId);
  const { state: grants, reload } = useRoleAssignments(placeKind, placeId);
  const [granting, setGranting] = useState<Granting>(null);
  const [grantingTo, setGrantingTo] = useState<GrantingTo>(null);
  const [inviting, setInviting] = useState(false);
  const titleId = useId();
  const canGrant = detail.can.grantRoles;
  useOpenGrantFromLink(params, setParams, canGrant, setGranting);

  const items = useMemo(() => (grants.status === 'error' ? [] : grants.items), [grants]);
  const roles = useRoleEntries(assignable.roles, items);
  const holdersByRole = useHoldersByRole(items);
  const set = useParamSetter(setParams);
  const selected = roles.find((role) => role.id === selectedRoleId) ?? null;
  const phoneRoleId = selected?.assignable?.id ?? null;

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-5">
      <RolesHead
        titleId={titleId}
        placeName={placeName}
        view={view}
        set={set}
        canGrant={canGrant}
        onGrant={() => setGranting({ roleId: phoneRoleId })}
      />
      <RolesAlerts show={canGrant && assignable.status === 'error'} onRetry={retryAssignable} />
      <RolesBody
        grants={grants}
        items={items}
        view={view}
        reload={reload}
        roles={roles}
        holdersByRole={holdersByRole}
        selected={selected}
        set={set}
        place={place}
        placeKind={placeKind}
        placeId={placeId}
        placeName={placeName}
        canGrant={canGrant}
        assignableKnown={assignable.status === 'ready'}
        onGrantRole={(roleId) => setGranting({ roleId })}
        onGrantPerson={setGrantingTo}
        onStanding={refreshPlace}
      />
      <RolesIncomplete show={grants.status === 'ready' && !grants.complete} />
      <RolesPanels
        granting={granting}
        setGranting={setGranting}
        grantingTo={grantingTo}
        setGrantingTo={setGrantingTo}
        assignable={assignable}
        retryAssignable={retryAssignable}
        holdersByRole={holdersByRole}
        items={items}
        place={place}
        placeKind={placeKind}
        placeId={placeId}
        placeName={placeName}
        canGrant={canGrant}
        canInvite={detail.can.invite}
        inviting={inviting}
        setInviting={setInviting}
        reload={reload}
        onStanding={refreshPlace}
        phoneRoleId={phoneRoleId}
      />
    </section>
  );
}
